import { useState, useMemo, useEffect, useRef, type FC } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/components/base/Toast';
import { submitEkyc, submitHtxEkyc, myLatestEkyc } from '@/api/ekyc';
import { uploadFile } from '@/api/file';
import { ApiError } from '@/api/client';
import EkycStepHeader from '@/components/feature/EkycStepHeader';
import IdPhotoCapture, { type CaptureType } from '@/components/feature/IdPhotoCapture';
import OcrReviewForm from '@/components/feature/OcrReviewForm';
import { ocrValid } from '@/components/feature/ocrValidation';
import type { OcrData } from '@/components/feature/OcrReviewForm';
import EkycResultScreen from '@/components/feature/EkycResultScreen';
import { extractDocument, type ExtractionJob } from '@/api/aiExtraction';
import { combineCardChecks, mergeCardReads } from '@/components/feature/ekycCardReading';

type FlowStep = 1 | 2 | 3 | 4 | 5 | 6 | 7;
type ResultType = 'verified' | 'need_more' | 'failed' | 'pending';

type Side = 'front' | 'back';
/** A CCCD side being read: uploaded the moment it is captured, then read by AI. */
type SideRead = { status: 'reading' } | { status: 'done' | 'failed'; job: ExtractionJob };
const OCR_KEYS = ['hoTen', 'soCccd', 'ngaySinh', 'gioiTinh', 'diaChi', 'ngayCap'] as const;


/** HTX doing eKYC on a farmer's behalf (e.g. a profile it created before the
 * farmer has an account). Same capture steps; the submission is for this
 * farmer, it passes at once and only adds to their credit score. */
export interface EkycOnBehalfOf {
  farmerId: string;
  farmerName: string;
  onDone: () => void;
}

const EkycFlow: FC<{ onBehalfOf?: EkycOnBehalfOf }> = ({ onBehalfOf }) => {
  const { user, reloadUser } = useAuth();
  // The flow is about whoever is being verified — a farmer when done on their
  // behalf, even though the person holding the phone is HTX staff.
  const role = onBehalfOf ? 'Nông hộ' : user?.role || 'Nông hộ';

  const [flowStep, setFlowStep] = useState<FlowStep>(1);
  type Photo = { blob: Blob; previewUrl: string } | null;
  const [front, setFront] = useState<Photo>(null);
  const [back, setBack] = useState<Photo>(null);
  const [selfie, setSelfie] = useState<Photo>(null);
  const [submitting, setSubmitting] = useState(false);
  const [resultType, setResultType] = useState<ResultType | null>(null);
  const [showOcrErrors, setShowOcrErrors] = useState(false);

  // Starts empty: the CCCD photos are read by AI (see startReading) and fill it.
  // It used to be filled with a random CCCD number and birth date, which is
  // why nothing submitted through eKYC could be checked against anything.
  const initialOcrData = useMemo<OcrData>(() => {
    const displayName = onBehalfOf?.farmerName || user?.displayName || '';
    const data: OcrData = { hoTen: '', soCccd: '', ngaySinh: '', gioiTinh: '', diaChi: '', ngayCap: '' };

    // An HTX submission is about its own cooperative/representative, not a
    // personal identity — the form carries these alongside the CCCD fields
    // (the representative's own CCCD is still what gets photographed and
    // reviewed; this is what an HTX-side reviewer additionally sees).
    if (!onBehalfOf && user?.role === 'Hợp tác xã') {
      data.tenHtx = displayName;
      data.maHtx = user.code || 'HTX-XXX-2025';
      data.vaiTroDaiDien = 'Đại diện hợp tác xã';
    }

    return data;
  }, [user, onBehalfOf]);

  const [ocrData, setOcrData] = useState<OcrData>(initialOcrData);

  const [reads, setReads] = useState<Partial<Record<Side, SideRead>>>({});
  // The upload started at capture, reused on submit when the photo is unchanged.
  const uploadsRef = useRef<Partial<Record<Side, { blob: Blob; fileId: Promise<string> }>>>({});
  const readSeq = useRef<Record<Side, number>>({ front: 0, back: 0 });
  // Values that came from AI (or a previous submission) and may be replaced by a
  // newer reading; anything the user typed is never overwritten.
  const replaceableRef = useRef<Partial<OcrData>>({});
  // What AI last read for each field — compared at submit to record manual edits.
  const aiReadRef = useRef<Partial<OcrData>>({});

  const startReading = (side: Side, blob: Blob) => {
    const seq = ++readSeq.current[side];
    const fileId = uploadFile(blob, side === 'front' ? 'EKYC_ID_FRONT' : 'EKYC_ID_BACK', blob.type || 'image/jpeg');
    // Submit awaits this again; a failure here must not surface as unhandled.
    fileId.catch(() => undefined);
    uploadsRef.current[side] = { blob, fileId };
    setReads((r) => ({ ...r, [side]: { status: 'reading' } }));
    void fileId
      .then((id) => extractDocument(id, side === 'front' ? 'CCCD_FRONT' : 'CCCD_BACK'))
      .catch(
        (): ExtractionJob => ({
          _id: '',
          fileId: '',
          documentType: side === 'front' ? 'CCCD_FRONT' : 'CCCD_BACK',
          status: 'FAILED',
          source: '',
          errorMessage: 'Không tải được ảnh lên để AI đọc.',
        }),
      )
      .then((job) => {
        if (readSeq.current[side] !== seq) return;
        setReads((r) => ({ ...r, [side]: { status: job.status === 'DONE' ? 'done' : 'failed', job } }));
      });
  };

  const capture = (side: Side, setPhoto: (p: Photo) => void) => (photo: Photo) => {
    setPhoto(photo);
    if (photo) {
      startReading(side, photo.blob);
      return;
    }
    readSeq.current[side]++;
    delete uploadsRef.current[side];
    setReads((r) => {
      const next = { ...r };
      delete next[side];
      return next;
    });
  };

  const jobOf = (r?: SideRead) => (r && r.status !== 'reading' ? r.job : undefined);
  const frontJob = jobOf(reads.front);
  const backJob = jobOf(reads.back);
  const reading = reads.front?.status === 'reading' || reads.back?.status === 'reading';
  const cardChecks = useMemo(() => combineCardChecks(frontJob, backJob), [frontJob, backJob]);
  const readNotice = (() => {
    if (frontJob?.source === 'MOCK' || backJob?.source === 'MOCK') {
      return 'Máy chủ chưa cấu hình AI — thông tin bên dưới là dữ liệu MẪU, không đọc từ ảnh. Hãy sửa lại cho đúng thẻ.';
    }
    const failed = [frontJob, backJob].filter((j) => j?.status === 'FAILED');
    if (failed.length > 0 && !reading) {
      return `${failed[0]?.errorMessage || 'AI không đọc được ảnh CCCD.'} Bấm "Chỉnh sửa" để tự nhập, hoặc quay lại chụp rõ hơn.`;
    }
    return undefined;
  })();

  // Each finished reading fills the fields still empty or still holding an
  // earlier AI value; a field the user typed stays as typed.
  useEffect(() => {
    const merged = mergeCardReads(frontJob?.status === 'DONE' ? frontJob.fields : undefined, backJob?.status === 'DONE' ? backJob.fields : undefined);
    const replaceable = replaceableRef.current;
    setOcrData((prev) => {
      const next = { ...prev };
      for (const key of OCR_KEYS) {
        const value = merged[key];
        if (value && (!prev[key] || prev[key] === replaceable[key])) next[key] = value;
      }
      return next;
    });
    replaceableRef.current = { ...replaceable, ...Object.fromEntries(OCR_KEYS.filter((k) => merged[k]).map((k) => [k, merged[k]])) };
    aiReadRef.current = merged;
  }, [frontJob, backJob]);

  // Re-entering the flow to correct a submission is an edit, not a fresh start:
  // seed the form from what was actually sent, otherwise "Chỉnh sửa hồ sơ" throws
  // the farmer's own details away and generates new mock ones.
  useEffect(() => {
    // "Mine" is the HTX's own record when acting on a farmer's behalf.
    if (onBehalfOf) return;
    let cancelled = false;
    myLatestEkyc()
      .then((record) => {
        const ocr = record?.ocrResult as Record<string, string | undefined> | undefined;
        if (cancelled || !ocr) return;
        const previous: Partial<OcrData> = {
          hoTen: ocr.name,
          soCccd: ocr.cccdMask,
          ngaySinh: ocr.dob,
          gioiTinh: ocr.gender,
          diaChi: ocr.address,
          ngayCap: ocr.issueDate,
        };
        // A fresh reading of new photos may replace these.
        replaceableRef.current = { ...previous, ...replaceableRef.current };
        setOcrData((prev) => {
          const next = { ...prev };
          for (const key of OCR_KEYS) if (!prev[key] && previous[key]) next[key] = previous[key]!;
          return next;
        });
      })
      // No prior submission, or it could not be read — the form starts empty.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [onBehalfOf]);

  const canProceed = (step: FlowStep): boolean => {
    switch (step) {
      case 2: return front !== null;
      case 3: return back !== null;
      case 4: return selfie !== null;
      default: return true;
    }
  };

  const handleNext = () => {
    // Every OCR field is required and format-checked before the submission can go on.
    if (flowStep === 5) {
      if (reading) {
        showToast('AI đang đọc ảnh CCCD — vui lòng chờ vài giây', 'info');
        return;
      }
      if (!ocrValid(ocrData, role)) {
        setShowOcrErrors(true);
        showToast('Vui lòng sửa các ô viền đỏ trước khi tiếp tục', 'warning');
        return;
      }
    }
    if (flowStep < 6) {
      setFlowStep((flowStep + 1) as FlowStep);
    }
  };

  const handleBack = () => {
    if (flowStep > 1) {
      setFlowStep((flowStep - 1) as FlowStep);
    }
  };

  /**
   * Sends the submission to the server and shows whatever it decided.
   *
   * This used to roll dice — Math.random() picked verified/need_more/failed/pending
   * and wrote the outcome to local state only. The backend never heard about the
   * submission, so the "HTX đang xem xét" screen described a queue nobody could
   * see and no HTX could act on.
   *
   * The server decides now: a farmer inside a cooperative comes back PENDING and
   * waits for that cooperative; a standalone farmer still auto-passes, having
   * nobody to review them.
   */
  const handleSubmit = async () => {
    const isHtx = !onBehalfOf && user?.role === 'Hợp tác xã';
    const farmerId = onBehalfOf?.farmerId ?? user?.farmerId;
    if (!isHtx && !farmerId) {
      showToast('Chưa có hồ sơ nông hộ để gửi eKYC', 'error');
      return;
    }
    if (!front || !back || !selfie) {
      showToast('Vui lòng chụp đủ mặt trước, mặt sau CCCD và ảnh chân dung', 'warning');
      return;
    }
    if (!ocrValid(ocrData, role)) {
      setShowOcrErrors(true);
      setFlowStep(5);
      showToast('Thông tin CCCD chưa hợp lệ — vui lòng sửa các ô viền đỏ', 'warning');
      return;
    }
    setSubmitting(true);
    try {
      // Upload the three real photos first; their ids are what the reviewer
      // opens. Before this, EkycFlow sent randomly generated ObjectIds that
      // referenced nothing — valid enough for @IsMongoId to accept, so the
      // submission was recorded with three file ids pointing at no file at all.
      // The CCCD photos already went up when captured (to be read); reuse those.
      const cardFileId = async (side: Side, photo: { blob: Blob }) => {
        const started = uploadsRef.current[side];
        if (started && started.blob === photo.blob) {
          try {
            return await started.fileId;
          } catch {
            // that upload failed — send it again below
          }
        }
        return uploadFile(photo.blob, side === 'front' ? 'EKYC_ID_FRONT' : 'EKYC_ID_BACK', photo.blob.type || 'image/jpeg');
      };
      const [idFrontFileId, idBackFileId, selfieFileId] = await Promise.all([
        cardFileId('front', front),
        cardFileId('back', back),
        uploadFile(selfie.blob, 'EKYC_SELFIE', selfie.blob.type || 'image/jpeg'),
      ]);

      // Keys the backend reads onto the Farmer/Htx record (see EkycService).
      // The full CCCD number goes only into `cccd`: the server encrypts it and
      // stores the masked form, never the number itself.
      const aiRead = aiReadRef.current;
      const ocrResult = {
        name: ocrData.hoTen,
        cccd: /^\d{12}$/.test(ocrData.soCccd) ? ocrData.soCccd : undefined,
        cccdMask: ocrData.soCccd,
        dob: ocrData.ngaySinh,
        gender: ocrData.gioiTinh,
        address: ocrData.diaChi,
        issueDate: ocrData.ngayCap,
        // For whoever reviews the identity: how it was read, what the checks
        // said, and which fields the user changed from what AI read.
        aiSource: frontJob?.source || backJob?.source || 'MANUAL',
        mrzVerified: backJob?.fields?.mrzVerified === true,
        aiChecks: cardChecks.map(({ code, ok, label }) => ({ code, ok, label })),
        editedAfterAi: OCR_KEYS.filter((k) => aiRead[k] && aiRead[k] !== ocrData[k]),
      };

      const record = isHtx
        ? await submitHtxEkyc({ idFrontFileId, idBackFileId, selfieFileId, ocrResult })
        : await submitEkyc({ farmerId: farmerId!, idFrontFileId, idBackFileId, selfieFileId, ocrResult });
      // Deliberately NOT reloading the session here. EkycPage renders this flow
      // only while kycStatus is 'registered', so refreshing now would swap the
      // component out mid-submit and the result screen would never be seen. The
      // refresh happens when the user acts on that screen.
      setResultType(record.decision === 'VERIFIED' ? 'verified' : 'pending');
      setFlowStep(7);
    } catch (e) {
      // Distinguish the upload leg from the submit leg: "ảnh không tải lên được"
      // and "hồ sơ bị từ chối" call for different things from the farmer.
      showToast(
        e instanceof ApiError
          ? e.message
          : 'Không tải được ảnh lên. Kiểm tra kết nối mạng và thử lại.',
        'error',
      );
    } finally {
      setSubmitting(false);
    }
  };

  /** Verified: adopt the server's state and EkycPage routes on into the app. */
  const handleEnterApp = async () => {
    if (onBehalfOf) {
      showToast(`Đã xác thực eKYC cho ${onBehalfOf.farmerName} — điểm tín nhiệm đã được cộng`, 'success');
      onBehalfOf.onDone();
      return;
    }
    await reloadUser();
    showToast('Xác thực thành công! Chào mừng bạn đến với Agrida.', 'success');
  };

  /** Waiting on the HTX: adopting the server's state renders the pending screen. */
  const handlePendingStay = async () => {
    if (onBehalfOf) {
      onBehalfOf.onDone();
      return;
    }
    await reloadUser();
    showToast('Hồ sơ đã gửi, đang chờ hợp tác xã phê duyệt.', 'info');
  };

  // Retry/resubmit only rewind this component. They used to write a fabricated
  // status — 'registered', or an invented 'ekyc_need_more' complete with a made-up
  // reason — which contradicted whatever the server held. Re-running the flow ends
  // in a real submission, and that is what changes status.
  const restartFlow = (message: string) => {
    setFlowStep(1);
    setFront(null);
    setBack(null);
    setSelfie(null);
    setResultType(null);
    readSeq.current = { front: readSeq.current.front + 1, back: readSeq.current.back + 1 };
    uploadsRef.current = {};
    replaceableRef.current = {};
    aiReadRef.current = {};
    setReads({});
    setShowOcrErrors(false);
    setOcrData(initialOcrData);
    showToast(message, 'info');
  };

  const handleRetry = () => restartFlow('Vui lòng thực hiện lại eKYC từ đầu.');

  const handleResubmit = () => restartFlow('Vui lòng chụp lại và gửi hồ sơ bổ sung.');

  const getStepTitle = (step: FlowStep): string => {
    const titles: Record<FlowStep, string> = {
      1: 'Xác thực danh tính',
      2: 'Chụp CCCD mặt trước',
      3: 'Chụp CCCD mặt sau',
      4: 'Chụp ảnh chân dung',
      5: 'Xác nhận thông tin OCR',
      6: 'Xác nhận gửi hồ sơ',
      7: 'Kết quả eKYC',
    };
    return titles[step];
  };

  const getStepSubtitle = (step: FlowStep): string => {
    const subtitles: Record<FlowStep, string> = {
      1: 'Agrida cần xác thực danh tính để đảm bảo an toàn và bảo mật cho tài khoản của bạn.',
      2: 'Vui lòng chụp ảnh mặt trước CCCD gắn chip. Đặt CCCD trên mặt phẳng, đủ ánh sáng.',
      3: 'Vui lòng chụp ảnh mặt sau CCCD. Đảm bảo thông tin rõ ràng, không bị mờ.',
      4: 'Chụp ảnh chân dung rõ mặt, không đeo kính đen, nhìn thẳng vào camera.',
      5: 'AI đọc thông tin từ ảnh CCCD và tự kiểm tra (mã MRZ, số CCCD). Vui lòng kiểm tra và sửa nếu cần.',
      6: 'Kiểm tra lại toàn bộ hồ sơ trước khi gửi. Thông tin phải chính xác và trùng khớp.',
      7: '',
    };
    return subtitles[step];
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b border-gray-100">
        <EkycStepHeader currentStep={flowStep} />
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="mb-5">
          <h3 className="text-lg font-bold text-gray-900">{getStepTitle(flowStep)}</h3>
          {flowStep !== 7 && (
            <p className="text-sm text-gray-500 mt-1">{getStepSubtitle(flowStep)}</p>
          )}
        </div>

        {flowStep === 1 && <StepIntro role={role} />}

        {([
          [2, 'front', front, capture('front', setFront)],
          [3, 'back', back, capture('back', setBack)],
          [4, 'selfie', selfie, setSelfie],
        ] as const).map(([step, type, photo, onCapture]) =>
          flowStep === step ? (
            <IdPhotoCapture key={type} type={type as CaptureType} photo={photo} onCapture={onCapture} />
          ) : null,
        )}

        {flowStep === 5 && (
          <OcrReviewForm
            data={ocrData}
            role={role}
            onChange={setOcrData}
            reading={reading}
            checks={cardChecks}
            notice={readNotice}
            showAllErrors={showOcrErrors}
          />
        )}

        {flowStep === 6 && <StepConfirm role={role} frontDone={front !== null} backDone={back !== null} selfieDone={selfie !== null} ocrData={ocrData} />}

        {flowStep === 7 && resultType && (
          <EkycResultScreen
            resultType={resultType}
            role={role}
            reason={onBehalfOf ? undefined : user?.kycReason}
            onEnterApp={handleEnterApp}
            onRetry={handleRetry}
            onResubmit={handleResubmit}
            onPendingStay={handlePendingStay}
          />
        )}
      </div>

      {/* Bottom CTA bar - only one main action per step */}
      {flowStep < 7 && (
        <div className="px-4 py-3 border-t border-gray-100 bg-white flex gap-3">
          {flowStep > 1 && (
            <button
              type="button"
              onClick={handleBack}
              className="px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors whitespace-nowrap min-h-[48px] flex items-center gap-1.5"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              Quay lại
            </button>
          )}
          {flowStep < 6 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceed(flowStep)}
              className="flex-1 bg-agrida-600 hover:bg-agrida-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl transition-colors disabled:cursor-not-allowed whitespace-nowrap min-h-[48px] text-sm flex items-center justify-center gap-2"
            >
              Tiếp tục
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 bg-agrida-600 hover:bg-agrida-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl transition-colors disabled:cursor-not-allowed whitespace-nowrap min-h-[48px] text-sm flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin" />
                  </span>
                  Đang xử lý...
                </>
              ) : (
                <>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-send-plane-line" />
                  </span>
                  Gửi hồ sơ eKYC
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

function StepIntro({ role }: { role: string }) {
  return (
    <div className="space-y-4">
      <div className="w-full aspect-[16/9] bg-gradient-to-br from-agrida-50 to-agrida-100 rounded-xl flex items-center justify-center mb-2">
        <div className="flex flex-col items-center gap-3">
          <span className="w-16 h-16 flex items-center justify-center">
            <i className="ri-shield-check-line text-agrida-400 text-5xl" />
          </span>
          <span className="text-agrida-600 text-sm font-semibold">Agrida eKYC</span>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
          <span className="w-8 h-8 rounded-xl bg-agrida-100 flex items-center justify-center shrink-0">
            <i className="ri-check-line text-agrida-600 text-sm" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Xác thực nhanh chóng</p>
            <p className="text-xs text-gray-500">Chỉ mất 3-5 phút để hoàn tất xác thực danh tính qua CCCD gắn chip.</p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
          <span className="w-8 h-8 rounded-xl bg-agrida-100 flex items-center justify-center shrink-0">
            <i className="ri-lock-line text-agrida-600 text-sm" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Bảo mật tuyệt đối</p>
            <p className="text-xs text-gray-500">Dữ liệu được mã hóa và chỉ sử dụng cho mục đích xác thực, không chia sẻ với bên thứ ba.</p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
          <span className="w-8 h-8 rounded-xl bg-agrida-100 flex items-center justify-center shrink-0">
            <i className="ri-bank-card-line text-agrida-600 text-sm" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Mở khóa tính năng</p>
            <p className="text-xs text-gray-500">
              {role === 'Nông hộ'
                ? 'Sau khi xác thực, bạn có thể vay vốn, nhận vật tư và ký hợp đồng với HTX.'
                : 'Sau khi xác thực, bạn có thể quản lý HTX, duyệt vay và ký hợp đồng với đối tác.'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-3 rounded-xl bg-amber-50 border border-amber-100">
        <div className="flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-500 text-xs" />
          </span>
          <div>
            <p className="text-xs font-medium text-amber-800">Cần chuẩn bị:</p>
            <ul className="mt-1 space-y-1">
              <li className="flex items-center gap-1.5 text-xs text-amber-700">
                <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                CCCD gắn chip còn hiệu lực
              </li>
              <li className="flex items-center gap-1.5 text-xs text-amber-700">
                <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                Điện thoại có camera hoạt động tốt
              </li>
              <li className="flex items-center gap-1.5 text-xs text-amber-700">
                <span className="w-1 h-1 rounded-full bg-amber-400 shrink-0" />
                Đủ ánh sáng, không bị chói
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepConfirm({ role, frontDone, backDone, selfieDone, ocrData }: {
  role: string;
  frontDone: boolean;
  backDone: boolean;
  selfieDone: boolean;
  ocrData: OcrData;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-xl bg-agrida-50 flex items-center justify-center">
          <i className="ri-file-check-line text-agrida-600 text-sm" />
        </span>
        <div>
          <p className="text-sm font-semibold text-gray-900">Tổng quan hồ sơ</p>
          <p className="text-[11px] text-gray-500">Kiểm tra lại trước khi gửi</p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`text-sm ${frontDone ? 'ri-checkbox-circle-fill text-agrida-500' : 'ri-checkbox-blank-circle-line text-gray-300'}`} />
            </span>
            <span className="text-sm text-gray-700">Ảnh CCCD mặt trước</span>
          </div>
          <span className={`text-xs font-medium ${frontDone ? 'text-agrida-600' : 'text-gray-400'}`}>
            {frontDone ? 'Đã chụp' : 'Chưa chụp'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`text-sm ${backDone ? 'ri-checkbox-circle-fill text-agrida-500' : 'ri-checkbox-blank-circle-line text-gray-300'}`} />
            </span>
            <span className="text-sm text-gray-700">Ảnh CCCD mặt sau</span>
          </div>
          <span className={`text-xs font-medium ${backDone ? 'text-agrida-600' : 'text-gray-400'}`}>
            {backDone ? 'Đã chụp' : 'Chưa chụp'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`text-sm ${selfieDone ? 'ri-checkbox-circle-fill text-agrida-500' : 'ri-checkbox-blank-circle-line text-gray-300'}`} />
            </span>
            <span className="text-sm text-gray-700">Ảnh chân dung</span>
          </div>
          <span className={`text-xs font-medium ${selfieDone ? 'text-agrida-600' : 'text-gray-400'}`}>
            {selfieDone ? 'Đã chụp' : 'Chưa chụp'}
          </span>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase mb-3">Thông tin OCR</p>
        <div className="space-y-2">
          <InfoRow label="Họ và tên" value={ocrData.hoTen} />
          <InfoRow label="Số CCCD" value={ocrData.soCccd} />
          <InfoRow label="Ngày sinh" value={ocrData.ngaySinh} />
          <InfoRow label="Giới tính" value={ocrData.gioiTinh} />
          <InfoRow label="Địa chỉ" value={ocrData.diaChi} />
          <InfoRow label="Ngày cấp" value={ocrData.ngayCap} />
        </div>
      </div>

      <div className="p-3 rounded-xl bg-agrida-50 border border-agrida-100">
        <div className="flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-agrida-600 text-xs" />
          </span>
          <p className="text-[11px] text-agrida-700">
            Bằng việc gửi hồ sơ, bạn xác nhận thông tin trên là chính xác và đồng ý với{' '}
            <strong>Điều khoản sử dụng</strong> và <strong>Chính sách bảo mật</strong> của Agrida.
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs font-semibold text-gray-900 text-right max-w-[60%] truncate">{value || '—'}</span>
    </div>
  );
}

export default EkycFlow;